import {
  CommonEntitiesListResponse,
  Insurance,
  InsuranceCreationDto,
  InsuranceResponseDto,
  InsuranceUpdateDto,
} from '@docbay/schemas';
import { fetchInsurancesProps } from 'common/types/insurances';
import axiosInstance from 'integration/api/axiosInstance';

/**
 * API for interacting with insurances.
 */
export const InsurancesAPI = {
  /**
   * Fetches a list of insurances.
   *
   * @param searchItem - Optional. The search query to use.
   * @param limit - Optional. The maximum number of results to return.
   * @param page - Optional. The page number to fetch.
   * @returns A promise that resolves with the response containing the list of insurances.
   */
  fetchInsurances: async ({
    searchItem,
    limit,
    page,
  }: fetchInsurancesProps) => {
    const params = {
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(searchItem ? { search: searchItem } : {}),
    };

    return await axiosInstance.get<CommonEntitiesListResponse<Insurance>>(
      '/insurances',
      { params },
    );
  },

  /**
   * Deletes a specific insurance.
   *
   * @param id - The ID of the insurance to delete.
   */
  deleteInsurances: (id: string) => axiosInstance.delete(`/insurances/${id}`),

  /**
   * Adds a new insurance.
   *
   * @param params - The data for the new insurance.
   * @returns A promise that resolves with the response containing the newly created insurance.
   */
  addInsurances: (params: InsuranceCreationDto) =>
    axiosInstance.post<InsuranceResponseDto>('/insurances', params),

  /**
   * Edits an existing insurance.
   *
   * @param data - The updated data for the insurance.
   * @param id - The ID of the insurance to update.
   * @returns A promise that resolves with the response containing the updated insurance.
   */
  editInsurances: ({ data, id }: { data: InsuranceUpdateDto; id: string }) =>
    axiosInstance.patch<InsuranceResponseDto>(`/insurances/${id}`, data),
};
